import { mapValues } from 'lodash-es'
import type {
  PermissionResource,
  PermissionSetResource,
} from '@/features/iam/api/resources/permissions'
import type { Permissions } from '@/features/iam/permissions/Permissions'

const permissionResources: Record<keyof Permissions, PermissionResource> = {
  adminAccess: 'ADMIN_ACCESS',
  adminAssignTeam: 'ADMIN_ASSIGN_TEAM',
  adminBillingManage: 'ADMIN_BILLING_MANAGE',
  adminClientImpersonate: 'ADMIN_CLIENT_IMPERSONATE',
  adminClientUserList: 'ADMIN_CLIENT_USER_LIST',
  adminConversationManage: 'ADMIN_CONVERSATION_MANAGE',
  adminDocumentManage: 'ADMIN_DOCUMENT_MANAGE',
  adminGlobalManageAccountMergeRequests:
    'ADMIN_GLOBAL_MANAGE_ACCOUNT_MERGE_REQUESTS',
  adminGlobalUserList: 'ADMIN_GLOBAL_USER_LIST',
  adminJobAmend: 'ADMIN_JOB_AMEND',
  adminJobChampionProviderManage: 'ADMIN_JOB_CHAMPION_PROVIDER_MANAGE',
  adminJobClientsManage: 'ADMIN_JOB_CLIENTS_MANAGE',
  adminJobClose: 'ADMIN_JOB_CLOSE',
  adminJobCreate: 'ADMIN_JOB_CREATE',
  adminJobDelete: 'ADMIN_JOB_DELETE',
  adminJobList: 'ADMIN_JOB_LIST',
  adminJobOnHold: 'ADMIN_JOB_ON_HOLD',
  adminJobProgressionApproveDraft: 'ADMIN_JOB_PROGRESSION_APPROVE_DRAFT',
  adminJobReopen: 'ADMIN_JOB_REOPEN',
  adminJobScopeChangeManage: 'ADMIN_JOB_SCOPE_CHANGE_MANAGE',
  adminJobScopeChangeResolve: 'ADMIN_JOB_SCOPE_CHANGE_RESOLVE',
  adminJobSupportingProviderManage: 'ADMIN_JOB_SUPPORTING_PROVIDER_MANAGE',
  adminJobTransfer: 'ADMIN_JOB_TRANSFER',
  adminJobUpdate: 'ADMIN_JOB_UPDATE',
  adminJobView: 'ADMIN_JOB_VIEW',
  adminLegendModify: 'ADMIN_LEGEND_MODIFY',
  adminLegendView: 'ADMIN_LEGEND_VIEW',
  adminManageGlobalInventoryItems: 'ADMIN_MANAGE_GLOBAL_INVENTORY_ITEMS',
  adminManageOcrJobs: 'ADMIN_MANAGE_OCR_JOBS',
  adminManageTeamMember: 'ADMIN_MANAGE_TEAM_MEMBER',
  adminProCutView: 'ADMIN_PRO_CUT_VIEW',
  adminProPiView: 'ADMIN_PRO_PI_VIEW',
  adminProviderImpersonate: 'ADMIN_PROVIDER_IMPERSONATE',
  adminRoleList: 'ADMIN_ROLE_LIST',
  adminScopeWorkView: 'ADMIN_SCOPE_WORK_VIEW',
  adminSkillsManage: 'ADMIN_SKILLS_MANAGE',
  adminSplitPayouts: 'ADMIN_SPLIT_PAYOUTS',
  adminStaffingManage: 'ADMIN_STAFFING_MANAGE',
  adminTeamsView: 'ADMIN_TEAMS_VIEW',
  adminUserCreate: 'ADMIN_USER_CREATE',
  adminUserList: 'ADMIN_USER_LIST',
  adminUserModify: 'ADMIN_USER_MODIFY',
  adminUserRead: 'ADMIN_USER_READ',
  adminUserRolesModify: 'ADMIN_USER_ROLES_MODIFY',
  adminViewPricing: 'ADMIN_VIEW_PRICING',
  adminViewTxfWorklayerJob: 'ADMIN_VIEW_TXF_WORKLAYER_JOB',
  chatAdd: 'CHAT_ADD',
  chatView: 'CHAT_VIEW',
  clientAssignmentManage: 'CLIENT_ASSIGNMENT_MANAGE',
  createTeam: 'CREATE_TEAM',
  createTeamJobs: 'CREATE_TEAM_JOBS',
  customerCreate: 'CUSTOMER_CREATE',
  docsClientRemove: 'DOCS_CLIENT_REMOVE',
  docsProviderRemove: 'DOCS_PROVIDER_REMOVE',
  docsSelfRemove: 'DOCS_SELF_REMOVE',
  docsUpload: 'DOCS_UPLOAD',
  docsView: 'DOCS_VIEW',
  insightsView: 'INSIGHTS_VIEW',
  jobClose: 'JOB_CLOSE',
  jobForfeit: 'JOB_FORFEIT',
  jobOnHold: 'JOB_ON_HOLD',
  jobPickup: 'JOB_PICKUP',
  jobProviderUpdateName: 'JOB_PROVIDER_UPDATE_NAME',
  jobScopeChangeCreate: 'JOB_SCOPE_CHANGE_CREATE',
  jobUpdate: 'JOB_UPDATE',
  manageBookkeeping: 'MANAGE_BOOKKEEPING',
  manageTeamBilling: 'MANAGE_TEAM_BILLING',
  manageTeamMember: 'MANAGE_TEAM_MEMBER',
  manageWorkspaceInventoryItems: 'MANAGE_WORKSPACE_INVENTORY_ITEMS',
  scopeWork: 'SCOPE_WORK',
  startNewJob: 'JOB_CREATE',
  startTeamJobs: 'START_TEAM_JOBS',
  supportingClientJobProgressionApproveDraft:
    'SUPPORTING_CLIENT_JOB_PROGRESSION_APPROVE_DRAFT',
  updateOwnSkills: 'UPDATE_OWN_SKILLS',
  updateTeamJobs: 'UPDATE_TEAM_JOBS',
  viewClientDetails: 'VIEW_CLIENT_DETAILS',
  viewContractAmount: 'VIEW_CONTRACT_AMOUNT',
  viewFirmDashboard: 'VIEW_FIRM_DASHBOARD',
  viewJobPricing: 'VIEW_PRICING',
  viewOpportunities: 'VIEW_OPPORTUNITIES',
  viewTeamJobs: 'VIEW_TEAM_JOBS',
  workspaceClientAssignmentManage: 'WORKSPACE_CLIENT_ASSIGNMENT_MANAGE',
  workspaceJobInfoGatheringUpdate: 'WORKSPACE_JOB_INFO_GATHERING_UPDATE',
  workspaceJobNoteManage: 'WORKSPACE_JOB_NOTE_MANAGE',
}

export function mapPermissionSetResourceToProps(
  resource: PermissionSetResource,
): Permissions {
  return mapValues(permissionResources, (p) => resource[p] === 'allow')
}

export function mapPermissionToResource(
  permission: keyof Permissions,
): PermissionResource {
  return permissionResources[permission]
}
